import { Link, createFileRoute } from "@tanstack/react-router";

import { AutoScale } from "../components/site/AutoScale";
import { Footer } from "../components/site/Footer";
import { Header } from "../components/site/Header";
import { ScrollToTop } from "../components/site/ScrollToTop";
import { SpecialOffer } from "../components/home/SpecialOffer";
import { formatDate, posts, publicDate } from "../content/blog";
import { SITE_URL } from "../lib/site";

const title = "Редакция HAY.agency — авторы блога";
const description =
  "Кто готовит материалы блога HAY.agency: экспертиза команды, темы публикаций, правила проверки фактов и обновления старых статей.";
const url = `${SITE_URL}/blog/authors/hay-agency`;
const image = `${SITE_URL}/og/blog.webp`;

export const Route = createFileRoute("/blog/authors/hay-agency")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "profile" },
      { property: "og:locale", content: "ru_RU" },
      { property: "og:url", content: url },
      { property: "og:image", content: image },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Блог HAY.agency" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: image },
      { name: "twitter:image:alt", content: "Блог HAY.agency" },
    ],
    links: [{ rel: "canonical", href: url }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "@id": `${url}#author`,
          name: "Редакция HAY.agency",
          url,
          parentOrganization: { "@type": "Organization", name: "HAY.agency", url: SITE_URL },
        }),
      },
    ],
  }),
  component: AuthorPage,
});

function AuthorPage() {
  return (
    <AutoScale>
      <div className="min-h-screen bg-background font-sans text-foreground antialiased">
        <SpecialOffer />
        <Header />
        <main className="px-5 pt-4 lg:px-[21px] lg:pt-[28px]">
          <nav
            aria-label="Хлебные крошки"
            className="flex flex-wrap items-center gap-1.5 text-[12px] leading-[16px] text-muted-foreground lg:gap-2 lg:text-[14px]"
          >
            <Link to="/" className="text-foreground underline underline-offset-[3px] hover:text-brand-text">
              Главная
            </Link>
            <span aria-hidden="true">/</span>
            <Link to="/blog" className="text-foreground underline underline-offset-[3px] hover:text-brand-text">
              Блог
            </Link>
            <span aria-hidden="true">/</span>
            <span>Редакция HAY.agency</span>
          </nav>

          <h1 className="section-title mt-8 lg:mt-[46px]">Редакция HAY.agency</h1>

          <div className="mt-8 max-w-[760px] lg:mt-10">
            <h2 className="text-[22px] font-medium leading-[28px] lg:text-[26px]">Кто готовит материалы</h2>
            <p className="mt-4 text-[16px] leading-[27px] text-foreground/85 lg:text-[17px] lg:leading-[29px]">
              Статьи готовит команда агентства: разработчики, SEO-специалисты, специалисты по контекстной и
              таргетированной рекламе, интеграторы CRM и проектные менеджеры. Пока у материала нет персонального
              автора, он выходит от имени редакции.
            </p>

            <h2 className="mt-10 text-[22px] font-medium leading-[28px] lg:text-[26px]">На чём основана экспертиза</h2>
            <p className="mt-4 text-[16px] leading-[27px] text-foreground/85 lg:text-[17px] lg:leading-[29px]">
              На проектах агентства с 2011 года: интернет-магазины и корпоративные сайты, продвижение в поиске и
              геосервисах, платный трафик, внедрение amoCRM, RetailCRM и Битрикс24. Практику дополняем официальной
              документацией площадок, о которых пишем.
            </p>

            <h2 className="mt-10 text-[22px] font-medium leading-[28px] lg:text-[26px]">Основные темы</h2>
            <ul className="mt-4 flex flex-col gap-3">
              {["E-commerce и маркетплейсы", "SEO и контентное продвижение", "Контекстная и таргетированная реклама", "Геосервисы и локальный маркетинг", "CRM и автоматизация продаж"].map((t) => (
                <li key={t} className="relative pl-5 text-[16px] leading-[27px] text-foreground/85 lg:text-[17px]">
                  <span className="absolute left-0 top-[11px] h-[6px] w-[6px] rounded-full bg-brand" />
                  {t}
                </li>
              ))}
            </ul>

            <h2 className="mt-10 text-[22px] font-medium leading-[28px] lg:text-[26px]">Как проверяем факты</h2>
            <p className="mt-4 text-[16px] leading-[27px] text-foreground/85 lg:text-[17px] lg:leading-[29px]">
              Цифры, тарифы, требования площадок и правовые нормы сверяем с первоисточником и указываем ссылку в блоке
              «Источники». Если данные могут измениться, фиксируем дату обращения к источнику. Оценки и мнение редакции
              отделяем от фактов.
            </p>

            <h2 className="mt-10 text-[22px] font-medium leading-[28px] lg:text-[26px]">Как обновляем старые статьи</h2>
            <p className="mt-4 text-[16px] leading-[27px] text-foreground/85 lg:text-[17px] lg:leading-[29px]">
              Дата публикации никогда не меняется. Дату обновления мы ставим только после реальной проверки и
              содержательной правки текста — техническая миграция или редизайн такой датой не сопровождаются. Подробнее
              — в{" "}
              <Link
                to="/blog/editorial-policy"
                className="underline underline-offset-[3px] transition-colors duration-300 hover:text-brand-text"
              >
                редакционной политике блога
              </Link>
              .
            </p>

            <h2 className="mt-10 text-[22px] font-medium leading-[28px] lg:text-[26px]">Статьи редакции</h2>
            <ul className="mt-4 flex flex-col gap-4">
              {posts.map((post) => (
                <li key={post.slug}>
                  <Link
                    to="/blog/$slug"
                    params={{ slug: post.slug }}
                    className="text-[17px] leading-[26px] underline underline-offset-[3px] transition-colors duration-300 hover:text-brand-text"
                  >
                    {post.title}
                  </Link>
                  {publicDate(post) ? (
                    <span className="body-12 ml-2 text-muted-foreground">{formatDate(publicDate(post)!)}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        </main>
        <div className="h-14 lg:h-[110px]" />
        <Footer />
        <ScrollToTop />
      </div>
    </AutoScale>
  );
}
